// Plain-English sentences for the Insurance "Re-run month" control.
// JSX-free so tests import it without a DOM.
//
// Reversibility-first (CLAUDE.md): before anything changes, the person sees
// the row as it is, the row as it will be, and what is lost or kept, as a
// sentence, never as a field diff.

import type { RerunPreview as InsuranceRerunPreview } from "@shared/comp/insurance-month-rerun-types";
import { formatCommissionPeriod } from "./admin-team-commission";
import { formatCents } from "../utils/money";

export interface RerunWords {
  /** "Today: Joy Smith, $5,000.00, calculated" — null when the month was never run. */
  today: string | null;
  /** "After the re-run: Brandon Ream, $17,835.57, in 2 paychecks of ..." — null when it cannot run. */
  after: string | null;
  /** Things the person must know before pressing the button. */
  warnings: string[];
  /** Whether the run button should be offered at all. */
  canRun: boolean;
  /** The label of the run button. */
  runLabel: string;
}

export function describeRerun(preview: InsuranceRerunPreview): RerunWords {
  const month = formatCommissionPeriod(preview.period);
  const { existing, proposed, blocker } = preview;

  const today = existing
    ? `${existing.advisor_name ?? "Unknown advisor"}, ${formatCents(existing.advisor_disbursement_cents)}, ${existing.status}`
    : null;

  const after = proposed
    ? `${proposed.advisor_name ?? "Unknown advisor"}, ${formatCents(proposed.advisor_disbursement_cents)}` +
      (proposed.paychecks_cents.length === 2
        ? ` (paychecks ${formatCents(proposed.paychecks_cents[0])} and ${formatCents(proposed.paychecks_cents[1])})`
        : "")
    : null;

  const warnings: string[] = [];
  if (blocker) warnings.push(blocker.message);
  if (existing && proposed && existing.advisor_user_id !== proposed.advisor_user_id) {
    warnings.push(
      `The row moves from ${existing.advisor_name ?? "the current advisor"} to ${proposed.advisor_name ?? "the new advisor"}.`,
    );
  }
  if (existing && (existing.status === "approved" || existing.status === "paid")) {
    warnings.push(
      `This month is already ${existing.status}. A re-run puts it back to "calculated", so it needs approving again${
        existing.status === "paid" ? " and nothing already paid is clawed back by the OS" : ""
      }.`,
    );
  }
  for (const st of preview.stale_statements ?? []) {
    warnings.push(
      `Also retires ${st.advisor_name ?? "the previous owner"}'s unsent ${month} statement, so it can't be sent by mistake. Undo brings it back.`,
    );
  }
  if (!existing) {
    warnings.push("Nothing exists for this month yet, so a re-run creates the row. There is nothing to undo back to.");
  }

  return {
    today,
    after,
    warnings,
    canRun: !blocker && proposed !== null,
    runLabel: `Re-run for ${month}`,
  };
}
